import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  MuiBox,
  MuiTypography,
  SidebarAccordion,
  SidebarAccordionItem,
} from "..";
import {
  accountBasePath,
  accountTeamMemberBasePath,
  adminMsgBasePath,
  clientUserBasePath,
  meetingReqBasePath,
  programmeDescriptorBasePath,
  referralBasePath,
  reportBasePath,
} from "../../aAdminTabTeam2/routes/routePaths";
import { MODULE_PERMISSIONS, PAGE_URLS } from "../../helpers/constants";
import {
  genericAccountsSiteCoreKey,
  navigationMenuSiteCoreKeys,
  reportMenuSiteCoreKey,
} from "../../helpers/constants/siteCoreKeys";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../hooks/usePermissions";
import { SidebarNewWrapper } from "./SidebarNew.style";
import { AccordionConfig, ProcessedItem } from "./models";

export const SidebarNew = () => {
  const { fetchLabel, fetchComponentTitle, fetchMediaListImage } =
    useNavigationSiteCore();
  const location = useLocation();
  const { hasPagePermission } = usePermissions();
  const [selectedDestination, setSelectedDestination] = useState<string>("");
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});

  const handleAccordionToggle = (id: string) => {
    setExpandedMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const dashboardArray: SidebarAccordionItem[] = [
    {
      destinationPath: "/dashboard",
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.navigationMenu,
        navigationMenuSiteCoreKeys.dashboard,
      ),
    },
  ];

  const clientsArray: SidebarAccordionItem[] = [
    {
      destinationPath: PAGE_URLS.SEARCH_CLIENT,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.clientManagement,
        navigationMenuSiteCoreKeys.searchClient,
      ),
    },
    {
      destinationPath: PAGE_URLS.CREATE_CLIENT,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.clientManagement,
        navigationMenuSiteCoreKeys.createClient,
      ),
    },
  ];

  const projectArray: SidebarAccordionItem[] = [
    {
      destinationPath: PAGE_URLS.SEARCH_PROJECT,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.project,
        navigationMenuSiteCoreKeys.searchProject,
      ),
    },
    {
      destinationPath: PAGE_URLS.CREATE_PROJECT,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.project,
        navigationMenuSiteCoreKeys.createProject,
      ),
    },
  ];

  const clientPortalSetupArray: SidebarAccordionItem[] = [
    {
      destinationPath: accountBasePath,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.clientPortalSubMenu,
        navigationMenuSiteCoreKeys.clientAccounts,
      ),
    },
    {
      destinationPath: reportBasePath,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.clientPortalSubMenu,
        navigationMenuSiteCoreKeys.reports,
      ),
    },
    {
      destinationPath: programmeDescriptorBasePath,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.clientPortalSubMenu,
        navigationMenuSiteCoreKeys.resources,
      ),
    },
    {
      destinationPath: referralBasePath,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.clientPortalSubMenu,
        navigationMenuSiteCoreKeys.referrals,
      ),
    },
    {
      destinationPath: clientUserBasePath,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.clientPortalSubMenu,
        navigationMenuSiteCoreKeys.clientUsers,
      ),
    },
    {
      destinationPath: accountTeamMemberBasePath,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.clientPortalSubMenu,
        navigationMenuSiteCoreKeys.accountTeam,
      ),
    },
    {
      destinationPath: adminMsgBasePath,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.clientPortalSubMenu,
        navigationMenuSiteCoreKeys.adminMessages,
      ),
    },
    {
      destinationPath: meetingReqBasePath,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.clientPortalSubMenu,
        navigationMenuSiteCoreKeys.meetingRequests,
      ),
    },
  ];

  const programAndCandidateDataArray: SidebarAccordionItem[] = [
    {
      destinationPath: PAGE_URLS.SEARCH_PROGRAM,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.programAndCandidates,
        navigationMenuSiteCoreKeys.searchProgranAndCandidate,
      ),
    },
    {
      destinationPath: PAGE_URLS.CREATE_PROGRAM,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.programAndCandidates,
        navigationMenuSiteCoreKeys.createProgram,
      ),
    },
    {
      destinationPath: PAGE_URLS.CREATE_NIS_PARTICIPANT,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.programAndCandidates,
        navigationMenuSiteCoreKeys.createNISParticipant,
      ),
    },
    {
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.programAndCandidates,
        navigationMenuSiteCoreKeys.bulkProcess,
      ), // Parent title for nested accordion
      destinationPath: "", // Not clickable itself
      children: [
        {
          destinationPath: PAGE_URLS.MANAGE_BULK_PROCESS,
          itemName: fetchLabel(
            navigationMenuSiteCoreKeys.programAndCandidates,
            navigationMenuSiteCoreKeys.manageBulkProcess,
          ),
        },
        {
          destinationPath: PAGE_URLS.BULK_CREATE_PROGRAM,
          itemName: fetchLabel(
            navigationMenuSiteCoreKeys.programAndCandidates,
            navigationMenuSiteCoreKeys.bulkCreateProgram,
          ),
        },
        {
          destinationPath: PAGE_URLS.BULK_EDIT_PROGRAM,
          itemName: fetchLabel(
            navigationMenuSiteCoreKeys.programAndCandidates,
            navigationMenuSiteCoreKeys.bulkEditProgram,
          ),
        },
        {
          destinationPath: PAGE_URLS.CREATE_BULK_TRANSFER,
          itemName: fetchLabel(
            navigationMenuSiteCoreKeys.programAndCandidates,
            navigationMenuSiteCoreKeys.bulkTransferParticipants,
          ),
        },
        {
          destinationPath: PAGE_URLS.CREATE_BULK_ACTIVITY,
          itemName: fetchLabel(
            navigationMenuSiteCoreKeys.programAndCandidates,
            navigationMenuSiteCoreKeys.bulkInsertActivity,
          ),
        },
      ],
    },
  ];

  const programTemplateArray: SidebarAccordionItem[] = [
    {
      destinationPath: PAGE_URLS.PROGRAM_TEMPLATE,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.programTemplateManagement,
        navigationMenuSiteCoreKeys.searchProgramTemplate,
      ),
    },
    {
      destinationPath: PAGE_URLS.CREATE_PROGRAM_TEMPLATE,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.programTemplateManagement,
        navigationMenuSiteCoreKeys.createProgramTemplate,
      ),
    },
  ];

  const masterDataArray: SidebarAccordionItem[] = [
    {
      destinationPath: PAGE_URLS.MDM_ACTIVITY,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.masterDataSubMenu,
        navigationMenuSiteCoreKeys.activity,
      ),
    },
    {
      destinationPath: PAGE_URLS.MDM_OFFICE,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.masterDataSubMenu,
        navigationMenuSiteCoreKeys.office,
      ),
    },
  ];

  const permissionAndMessagesArray: SidebarAccordionItem[] = [
    {
      destinationPath: PAGE_URLS.SEARCH_USERS,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.permissionAndMessagesSubmenu,
        navigationMenuSiteCoreKeys.usersSubmenu,
      ),
    },
    {
      destinationPath: PAGE_URLS.SYSTEM_MESSAGES,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.permissionAndMessagesSubmenu,
        navigationMenuSiteCoreKeys.systemMessages,
      ),
    },
  ];

  const reportsArray: SidebarAccordionItem[] = [
    {
      destinationPath: "/reports",
      itemName: fetchComponentTitle(reportMenuSiteCoreKey.ReportsSubMenu),
    },
  ];

  const coachPortalArray: SidebarAccordionItem[] = [
    {
      destinationPath: PAGE_URLS.COACH_PORTAL_DASHBOARD,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.coachPortalManagement,
        navigationMenuSiteCoreKeys.coachPortalDashboard,
      ),
    },
    {
      destinationPath: PAGE_URLS.COACH_PORTAL_SEARCH_PARTICIPANTS,
      itemName: fetchLabel(
        navigationMenuSiteCoreKeys.coachPortalManagement,
        navigationMenuSiteCoreKeys.coachPortalParticipants,
      ),
    },
  ];

  const genericAccountsArray: SidebarAccordionItem[] = [
    {
      destinationPath: PAGE_URLS.GENERIC_ACCOUNTS_SEARCH,
      itemName: fetchLabel(
        genericAccountsSiteCoreKey.genericAccountsSubMenu,
        genericAccountsSiteCoreKey.searchGenericAccounts,
      ),
    },
    {
      destinationPath: PAGE_URLS.CREATE_GENERIC_ACCOUNTS,
      itemName: fetchLabel(
        genericAccountsSiteCoreKey.genericAccountsSubMenu,
        genericAccountsSiteCoreKey.createNewGenericAccount,
      ),
    },
  ];

  useEffect(() => {
    setSelectedDestination(location.pathname);
  }, [location.pathname]);

  const getFetchedMediaListImage = (menuName: string, logo: string) => {
    return fetchMediaListImage(
      menuName,
      logo,
      navigationMenuSiteCoreKeys.smallImage,
    );
  };

  // Fetching media list images for icons
  const dashboardExpandModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.navigationMenu,
    navigationMenuSiteCoreKeys.dashboardExpandModeIcon,
  );
  const dashboardCollapseModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.navigationMenu,
    navigationMenuSiteCoreKeys.dashboardCollapseModeIcon,
  );
  const clientManagementExpandModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.clientManagement,
    navigationMenuSiteCoreKeys.clientManagementExpandModeIcon,
  );
  const clientManagementCollapseModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.clientManagement,
    navigationMenuSiteCoreKeys.clientManagementCollapseModeIcon,
  );
  const projectExpandModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.project,
    navigationMenuSiteCoreKeys.projectExpandModeIcon,
  );
  const projectCollapseModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.project,
    navigationMenuSiteCoreKeys.projectCollapseModeIcon,
  );
  const programsAndParticipantsExpandModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.programAndCandidates,
    navigationMenuSiteCoreKeys.programsAndParticipantsExpandModeIcon,
  );
  const programsAndParticipantsCollapseModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.programAndCandidates,
    navigationMenuSiteCoreKeys.programsAndParticipantsCollapseModeIcon,
  );
  const programTemplateManagementExpandModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.programTemplateManagement,
    navigationMenuSiteCoreKeys.programTemplateManagementExpandModeIcon,
  );
  const programTemplateManagementCollapseModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.programTemplateManagement,
    navigationMenuSiteCoreKeys.programTemplateManagementCollapseModeIcon,
  );
  const masterDataExpandModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.masterDataSubMenu,
    navigationMenuSiteCoreKeys.masterDataExpandModeIcon,
  );
  const masterDataCollapseModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.masterDataSubMenu,
    navigationMenuSiteCoreKeys.masterDataCollapseModeIcon,
  );
  const clientPortalSetupExpandModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.clientPortalSubMenu,
    navigationMenuSiteCoreKeys.clientPortalSetupExpandModeIcon,
  );
  const clientPortalSetupCollapseModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.clientPortalSubMenu,
    navigationMenuSiteCoreKeys.clientPortalSetupCollapseModeIcon,
  );
  const coachPortalParticipantsExpandModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.coachPortalParticipantsManagement,
    navigationMenuSiteCoreKeys.coachPortalParticipantsExpandModeIcon,
  );
  const coachPortalParticipantsCollapseModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.coachPortalParticipantsManagement,
    navigationMenuSiteCoreKeys.coachPortalParticipantsCollapseModeIcon,
  );
  const reportExtendModeIcon = getFetchedMediaListImage(
    reportMenuSiteCoreKey.ReportsSubMenu,
    reportMenuSiteCoreKey.expandModeIcon,
  );
  const reportCollapseModeIcon = getFetchedMediaListImage(
    reportMenuSiteCoreKey.ReportsSubMenu,
    reportMenuSiteCoreKey.collapseModeIcon,
  );
  const permissionsAndMessagesExpandModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.permissionAndMessagesSubmenu,
    navigationMenuSiteCoreKeys.permissionAndMessagesExpandModeIcon,
  );
  const permissionsAndMessagesCollapseModeIcon = getFetchedMediaListImage(
    navigationMenuSiteCoreKeys.permissionAndMessagesSubmenu,
    navigationMenuSiteCoreKeys.permissionAndMessagesCollapseModeIcon,
  );

  const genericAccountsExpandModeIcon = getFetchedMediaListImage(
    genericAccountsSiteCoreKey.genericAccountsSubMenu,
    genericAccountsSiteCoreKey.expandModeIcon,
  );
  const genericAccountsCollapseModeIcon = getFetchedMediaListImage(
    genericAccountsSiteCoreKey.genericAccountsSubMenu,
    genericAccountsSiteCoreKey.collapseModeIcon,
  );

  const accordionConfigs: AccordionConfig[] = [
    {
      type: "accordion",
      id: "dashboard",
      titleKey: navigationMenuSiteCoreKeys.navigationMenu,
      items: dashboardArray,
      expandIcon: dashboardExpandModeIcon,
      collapseIcon: dashboardCollapseModeIcon,
      showAccordion: false,
    },
    {
      type: "accordion",
      id: "client",
      titleKey: navigationMenuSiteCoreKeys.clientManagement,
      items: clientsArray,
      expandIcon: clientManagementExpandModeIcon,
      collapseIcon: clientManagementCollapseModeIcon,
    },
    {
      type: "accordion",
      id: "programAndCandidates",
      titleKey: navigationMenuSiteCoreKeys.programAndCandidates,
      items: programAndCandidateDataArray,
      expandIcon: programsAndParticipantsExpandModeIcon,
      collapseIcon: programsAndParticipantsCollapseModeIcon,
    },
    {
      type: "accordion",
      id: "project",
      titleKey: navigationMenuSiteCoreKeys.project,
      items: projectArray,
      expandIcon: projectExpandModeIcon,
      collapseIcon: projectCollapseModeIcon,
    },
    {
      type: "accordion",
      id: "genericAccounts",
      titleKey: genericAccountsSiteCoreKey.genericAccountsSubMenu,
      items: genericAccountsArray,
      expandIcon: genericAccountsExpandModeIcon,
      collapseIcon: genericAccountsCollapseModeIcon,
    },
    {
      type: "accordion",
      id: "programTemplate",
      titleKey: navigationMenuSiteCoreKeys.programTemplateManagement,
      items: programTemplateArray,
      expandIcon: programTemplateManagementExpandModeIcon,
      collapseIcon: programTemplateManagementCollapseModeIcon,
    },
    {
      type: "accordion",
      id: "reports",
      titleText: reportMenuSiteCoreKey.ReportsSubMenu,
      items: reportsArray,
      expandIcon: reportExtendModeIcon,
      collapseIcon: reportCollapseModeIcon,
      showAccordion: false,
    },
    {
      type: "accordion",
      id: "participants",
      // TO-DO Need to remove this static once RBAC is implemented.
      titleKey: navigationMenuSiteCoreKeys.coachPortalManagement,
      items: coachPortalArray,
      expandIcon: coachPortalParticipantsExpandModeIcon,
      collapseIcon: coachPortalParticipantsCollapseModeIcon,
    },
    {
      type: "title",
      id: "dataAndSettingsTitle",
      titleKey: navigationMenuSiteCoreKeys.navigationMenu,
      labelKey: navigationMenuSiteCoreKeys.dataAndSettings,
    },
    {
      type: "accordion",
      id: "permissionAndMessages",
      titleKey: navigationMenuSiteCoreKeys.permissionAndMessagesSubmenu,
      items: permissionAndMessagesArray,
      expandIcon: permissionsAndMessagesExpandModeIcon,
      collapseIcon: permissionsAndMessagesCollapseModeIcon,
    },
    {
      type: "accordion",
      id: "masterData",
      titleKey: navigationMenuSiteCoreKeys.masterDataSubMenu,
      items: masterDataArray,
      expandIcon: masterDataExpandModeIcon,
      collapseIcon: masterDataCollapseModeIcon,
    },
    ...(hasPagePermission(MODULE_PERMISSIONS.CLIENT_PORTAL_SETUP_VIEW)
      ? [
          {
            type: "accordion" as const,
            id: "clientPortalSetup",
            titleKey: navigationMenuSiteCoreKeys.clientPortalSubMenu,
            items: clientPortalSetupArray,
            expandIcon: clientPortalSetupExpandModeIcon,
            collapseIcon: clientPortalSetupCollapseModeIcon,
          },
        ]
      : []),
  ];

  const filterSidebarItem = (
    item: SidebarAccordionItem,
  ): SidebarAccordionItem | null => {
    if (item.children && item.children.length > 0) {
      const allowedChildren = item.children.filter((child) =>
        hasPagePermission(child.destinationPath),
      );
      return allowedChildren.length > 0
        ? { ...item, children: allowedChildren }
        : null;
    }
    return hasPagePermission(item.destinationPath) ? item : null;
  };

  const processAccordionConfig = (
    config: AccordionConfig,
  ): ProcessedItem | null => {
    if (config.type === "title") {
      return {
        ...config,
        titleLabel: fetchLabel(config.titleKey, config.labelKey),
      } as ProcessedItem;
    }

    if (config.type === "accordion") {
      const filteredItems = (config.items || [])
        .map(filterSidebarItem)
        .filter(Boolean) as SidebarAccordionItem[];

      if (filteredItems.length === 0) return null;

      const isExpanded = !!expandedMap[config.id];
      return {
        ...config,
        items: filteredItems,
        isExpanded,
        imgSrc: isExpanded
          ? config.expandIcon?.ImageUrl
          : config.collapseIcon?.ImageUrl,
        imgAltText: isExpanded
          ? config.expandIcon?.AltText
          : config.collapseIcon?.AltText,
        accordionTitle: config.titleText
          ? config.titleText
          : fetchComponentTitle(config.titleKey!),
      } as ProcessedItem;
    }

    return null;
  };

  const processedAccordions: ProcessedItem[] = accordionConfigs
    .map(processAccordionConfig)
    .filter(Boolean) as ProcessedItem[];

  return (
    <SidebarNewWrapper>
      <MuiBox className="container">
        {processedAccordions.map((item) => {
          if (item.type === "title") {
            return (
              <MuiTypography
                key={item.id}
                variant="h5Bold"
                className="additional-data-title"
              >
                {item.titleLabel}
              </MuiTypography>
            );
          }

          return (
            <SidebarAccordion
              key={item.id}
              id={item.id}
              expanded={item.isExpanded}
              onAccordionToggle={handleAccordionToggle}
              accordionTitle={item.accordionTitle}
              accordionItemArray={item.items}
              selectedDestination={selectedDestination}
              imgSrc={item.imgSrc}
              imgAltText={item.imgAltText}
              showAccordion={item.showAccordion ?? true}
            />
          );
        })}
      </MuiBox>
    </SidebarNewWrapper>
  );
};
